/**
 * Role-permissions repository — data access for the three tables of the
 * two-layer permission model (PERMISSIONS.md): `role_permissions` (the
 * permission roles + their permission arrays), `user_roles` (profile type +
 * custom_role link) and `user_role_assignments` (role_id ↔ user link).
 *
 * Strangler-Fig per-slice migration (manifest-6, fe-tail T8):
 *   Step 1 (HERE) — consolidated the scattered role-management calls from
 *                   `useSettings`, `UsersRolesTab` and `EditEmployeeDialog`.
 *                   Query shapes are BYTE-IDENTICAL to the inline originals —
 *                   including the read methods that deliberately swallow
 *                   errors (their hooks never destructured `error`).
 *   Step 2 (deferred) — role-management Nest endpoints are a post-M7
 *                   class-B follow-on; every method is Supabase-only.
 *
 * N-6 owns the permission *gate* (read path — `usePermissions`); this file
 * owns role *management* only and must never be imported by usePermissions.
 *
 * Auth / RLS: staff-side, company-scoped — every method takes an explicit
 * `companyId`; RLS is the authoritative gate underneath.
 */

import { supabase } from "@/integrations/supabase/client";
import type { Json, Tables } from "@/integrations/supabase/types";
import type { PostgrestError } from "@supabase/supabase-js";

export type RolePermissionRow = Tables<"role_permissions">;

export interface RolePermissionCreate {
  role_name: string;
  permissions: string[];
  portal_type?: string;
}

export interface RolePermissionPatch {
  id: string;
  permissions: string[];
  portal_type?: string;
}

export interface UserRoleAssignmentRef {
  user_id: string;
  role_id: string;
}

export const rolePermissionsRepository = {
  /** All permission roles of the company, ordered by role name. */
  async listForCompany(companyId: string): Promise<RolePermissionRow[]> {
    const { data, error } = await supabase
      .from("role_permissions")
      .select("*")
      .eq("company_id", companyId)
      .order("role_name");
    if (error) throw error;
    return data || [];
  },

  /** Create a permission role for the company. */
  async create(
    companyId: string,
    data: RolePermissionCreate,
  ): Promise<RolePermissionRow> {
    const { data: result, error } = await supabase
      .from("role_permissions")
      .insert({
        company_id: companyId,
        role_name: data.role_name,
        permissions: data.permissions as unknown as Json,
        ...(data.portal_type !== undefined && {
          portal_type: data.portal_type,
        }),
      })
      .select()
      .maybeSingle();
    if (error) throw error;
    if (!result) throw new Error("role_permissions insert returned no row");
    return result;
  },

  /** Update a role's permissions (and optionally portal), scoped by id AND company_id. */
  async update(
    companyId: string,
    data: RolePermissionPatch,
  ): Promise<RolePermissionRow | null> {
    const { data: result, error } = await supabase
      .from("role_permissions")
      .update({
        permissions: data.permissions as unknown as Json,
        ...(data.portal_type !== undefined && {
          portal_type: data.portal_type,
        }),
      })
      .eq("id", data.id)
      .eq("company_id", companyId)
      .select()
      .maybeSingle();
    if (error) throw error;
    return result;
  },

  /** Delete a role, scoped by id AND company_id. */
  async remove(id: string, companyId: string): Promise<void> {
    const { error } = await supabase
      .from("role_permissions")
      .delete()
      .eq("id", id)
      .eq("company_id", companyId);
    if (error) throw error;
  },

  /** Clone a role under a new name — always lands as a non-system role. */
  async clone(
    companyId: string,
    sourceRole: Pick<RolePermissionRow, "permissions" | "portal_type">,
    newName: string,
  ): Promise<RolePermissionRow> {
    const { data: result, error } = await supabase
      .from("role_permissions")
      .insert({
        company_id: companyId,
        role_name: newName,
        permissions: sourceRole.permissions as unknown as Json,
        portal_type: sourceRole.portal_type,
        is_system_role: false,
      })
      .select()
      .maybeSingle();
    if (error) throw error;
    if (!result) throw new Error("role_permissions insert returned no row");
    return result;
  },

  /** How many user_role_assignments reference a role (delete confirmation). */
  async countAssignments(roleId: string, companyId: string): Promise<number> {
    const { count, error } = await supabase
      .from("user_role_assignments")
      .select("id", { count: "exact", head: true })
      .eq("role_id", roleId)
      .eq("company_id", companyId);
    if (error) throw error;
    return (count as number) ?? 0;
  },

  /**
   * user_id → role_id pairs for a set of users (bulk role-column resolution
   * in the employees table). Errors are swallowed (empty result) — exactly
   * as the inline original did.
   */
  async assignmentsForUsers(
    userIds: string[],
    companyId: string,
  ): Promise<UserRoleAssignmentRef[]> {
    const { data } = await supabase
      .from("user_role_assignments")
      .select("user_id, role_id")
      .in("user_id", userIds)
      .eq("company_id", companyId);
    return data ?? [];
  },

  /**
   * The assigned role_id for one user (fallback when the employee row has no
   * role_permission_id). Errors are swallowed (null) — as the inline original.
   */
  async assignedRoleIdForUser(
    userId: string,
    companyId: string,
  ): Promise<string | null> {
    const { data } = await supabase
      .from("user_role_assignments")
      .select("role_id")
      .eq("user_id", userId)
      .eq("company_id", companyId)
      .limit(1)
      .maybeSingle();
    return data?.role_id ?? null;
  },

  /**
   * user_ids holding the `admin` / `super_admin` profile roles in the
   * company (super-admin promote/demote toggle). Errors are swallowed
   * (empty sets) — as the original.
   *
   * A user can hold BOTH an "admin" and a "super_admin" user_roles row
   * (multi-role-per-company is intended) — the two sets are tracked
   * separately so callers can exclude super_admin rows from admin-only
   * actions: rpc_revoke_admin rejects demoting a super_admin
   * (target_is_super_admin), so offering the Demote button on those rows
   * is always a dead-end action (Sentry DALLALI-FRONTEND-FV).
   */
  async adminAndSuperAdminUserIds(
    companyId: string,
  ): Promise<{ adminUserIds: string[]; superAdminUserIds: string[] }> {
    const { data } = await supabase
      .from("user_roles")
      .select("user_id, role")
      .eq("company_id", companyId)
      .in("role", ["admin", "super_admin"]);
    const adminUserIds: string[] = [];
    const superAdminUserIds: string[] = [];
    (data ?? []).forEach((r: { user_id: string; role: string }) => {
      if (r.role === "admin") adminUserIds.push(r.user_id);
      if (r.role === "super_admin") superAdminUserIds.push(r.user_id);
    });
    return { adminUserIds, superAdminUserIds };
  },

  /**
   * Sync an employee's custom_role on their user_roles row (the permission
   * layer behind hasPermission()). Scoped to the employee-portal row only —
   * one person can hold several user_roles rows in the same company (e.g.
   * both "employee" and "owner"); without the role filter the UPDATE hits
   * every row and validate_custom_role_matches_company() rejects it for the
   * non-employee rows. See Sentry #2894 / get_user_permissions union.
   *
   * Returns the error instead of throwing — the caller toasts + logs without
   * aborting the surrounding employee update (inline-original semantics).
   */
  async syncEmployeeCustomRole(
    userId: string,
    companyId: string,
    customRole: string,
  ): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase
      .from("user_roles")
      .update({ custom_role: customRole })
      .eq("user_id", userId)
      .eq("company_id", companyId)
      .eq("role", "employee");
    return { error };
  },
};
